'use strict'

const { section, saving } = require('./sections')

const scheduledTasks = {
  title: 'Scheduled tasks',
  intro: 'Scheduled tasks run an action on this spreadsheet at a time you choose. This dialog is the list. Creating and editing happen in the next dialog.',
  sections: [
    section('The list', [
      'Each row is one task: its title, the action it performs, the schedule in words, the last time it ran, the next time it will run, how many times it has run, and whether it is active or paused.',
      'A paused task stays in the list and does not run. Edit the task and turn Enabled off to pause it, or on to resume it. The status badge is that flag.',
      'Edit opens the task. Logs opens the run history for that task only. The trash button asks you to confirm before the task and its logs are removed.',
      'New Schedule opens a blank task. Run Logs opens the history of every task on this spreadsheet.',
      'An empty list means this spreadsheet has no tasks. Tasks belong to the spreadsheet that was open when you clicked the schedule button. They do not run against a different sheet.',
      'There is no OK button. The list reloads when you save or delete a task. Close the dialog when you are done.'
    ])
  ]
}

const scheduledTaskForm = {
  title: 'Scheduled task',
  intro: 'This dialog defines one task: what it does to the spreadsheet, when it runs, and whether it is allowed to run.',
  sections: [
    section('Title', [
      'Task title is the name in the list and in the logs. Use a name that says what the run does, such as a nightly status change, because the log does not repeat the filter in the title column.'
    ]),
    section('Bulk update', [
      'Bulk update writes values onto records that match the filter. The filter builder chooses the records. All means every condition must match. Any means one condition is enough. No conditions means every record on the spreadsheet.',
      'Only boxes you fill are written. An empty box leaves the current value. Number, date time, text, and multiselect follow the column type. A multiselect without a choice list is comma-separated text. A multiselect can append or remove one value. A relationship can append or remove one record id.',
      'The update runs later, when the schedule fires. It does not run when you click Save, except that Save stores the task.'
    ]),
    section('Bulk delete', [
      'Bulk delete permanently deletes records that match the filter, at the time the task runs. The warning in the dialog is that sentence.',
      'A filter with no conditions matches every record. Read the filter before you enable a delete task. The records are not moved to a trash. History can show that they were deleted. It does not put them back.'
    ]),
    section('Recalculate formulas', [
      'Recalculate formulas recomputes every formula column on this spreadsheet. There is no filter and no value list. Use it when formulas depend on data that changes outside a normal record save, or when you want a regular refresh.',
      'The info line in the dialog states that all formula columns are included.'
    ]),
    section('Trigger an automation', [
      'Automation is the automation to run. Trigger label is the name recorded for the run, so logs can tell one use of the automation from another.',
      'Automation variables replace {{name}} placeholders in the automation when the schedule fires. Each box is the value for that variable. The label shows the variable name and where the automation uses it. A schedule does not have a form field to map, so you type the value here.',
      'The automation runs with those values even if no record matched anything. It is not, by itself, a per-record loop. The automation\'s own steps decide what it reads and writes.'
    ]),
    section('When it runs', [
      'Run once uses the date and time in Run at. After that run, the task does not repeat.',
      'Recurring uses a frequency. Every minute and hourly run on that cadence. Daily, weekly, monthly, and yearly also ask for a time of day. Weekly asks for a weekday. Monthly asks for a day of the month, from 1 to 31. Yearly asks for a month and a day. A day the month does not have, such as the 31st in February, runs on the last day of that month and returns to the 31st in months that have it.',
      'Custom interval is a number and a unit. Every 15 minutes is 15 and minutes. The task waits that long after a run before the next one.',
      'Enabled lets the task run. Uncheck it to keep the definition and stop the clock. A disabled task shows as paused in the list.',
      'Save stores the task. The next run time updates from the schedule you set. An error alert explains a schedule the server rejected.'
    ]),
    saving('Save', 'the scheduled task')
  ]
}

const scheduledTaskLogs = {
  title: 'Scheduled task logs',
  intro: 'Logs are the record of runs. This dialog does not re-run a task and does not change one.',
  sections: [
    section('Columns', [
      'Task is the title at the time you are reading the log. Action is bulk update, bulk delete, recalculate formulas, or trigger automation. Started and finished are the timestamps. Duration is the time between them. A run that has not finished shows a dash for the end time.',
      'Records affected is how many rows the action changed or removed. An automation or a formula recalculation can show zero when nothing was written. Status is success or the failure state. Error is the message when the run failed, and blank when it succeeded.',
      'Opened from one task, the list is that task. Opened from Run Logs, the list is every task on this spreadsheet.',
      'There is no OK button. Close the dialog when you are done. Deleting a task also deletes its logs, which is stated again on the delete dialog.'
    ])
  ]
}

const deleteScheduledTask = {
  title: 'Delete a scheduled task',
  intro: 'This dialog removes one task and the history of its runs.',
  sections: [
    section('What is removed', [
      'The task will not run again. Its title, filter, values, and schedule are deleted. Every run log for this task is deleted with it. Logs for other tasks stay.',
      'Records the task already changed are not reverted. A bulk update that already ran stays on the records. A bulk delete that already ran is not undone.',
      'Delete confirms the removal. Cancel leaves the task and its logs in place.'
    ]),
    saving('Delete', 'the removal of this task and its logs')
  ]
}

module.exports = {
  'scheduled-tasks': scheduledTasks,
  'scheduled-task-form': scheduledTaskForm,
  'scheduled-task-logs': scheduledTaskLogs,
  'delete-scheduled-task': deleteScheduledTask
}
